import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  CircularProgress,
  FormLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { addRecommendations } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import RichtextEditor from '../../../common/RichtextEditor';

export default function AddRecommendationModal({ facilityId, quarter, year, onSuccess }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newActions, setNewActions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleOpenDialog = () => setIsDialogOpen(true);

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setNewComment('');
    setNewActions('');
  };

  // Helper to check if Rich Text Editor string contains actual text content
  const isContentEmpty = (htmlString) => {
    if (!htmlString) return true;
    const plainText = htmlString.replace(/<[^>]*>/g, '').trim();
    return !plainText;
  };

  const handleSubmit = async () => {
    if (isContentEmpty(newComment)) {
      openNotification({ type: 'error', message: 'Please fill in the Comment field.' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        facilityId,
        reportQuarter: quarter,
        reportYear: year,
        comment: newComment,
        actions: newActions,
      };

      const res = await addRecommendations(payload);
      const { status } = res?.result || {};

      if (status?.code === '00') {
        openNotification({
          type: 'success',
          message: status?.desc || 'Recommendation added successfully',
        });
        handleCloseDialog();
        if (onSuccess) onSuccess();
      } else {
        openNotification({
          type: 'error',
          message: status?.desc || 'Failed to add recommendation',
        });
      }
    } catch (error) {
      console.error('Error adding recommendation:', error);
      openNotification({
        type: 'error',
        message: 'Failed to add recommendation. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isFormInvalid = isContentEmpty(newComment);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpenDialog}
        sx={{
          textTransform: 'none',
          borderRadius: 2,
          px: 2.5,
          py: 1,
          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(25, 118, 210, 0.35)',
          },
        }}
      >
        Add Recommendation
      </Button>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
        aria-labelledby="add-recommendation-dialog-title"
      >
        <DialogTitle id="add-recommendation-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="700">
              Add New Recommendation
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small" sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: 'divider' }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Box display="flex" flexDirection="column" gap={1}>
              <FormLabel
                required
                sx={{ fontWeight: '600', color: 'text.primary', fontSize: '0.9rem' }}
              >
                Comment
              </FormLabel>
              <RichtextEditor value={newComment} onChange={setNewComment} />
              <Typography variant="caption" color="text.secondary">
                Describe the observation or issue.
              </Typography>
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              <FormLabel sx={{ fontWeight: '600', color: 'text.primary', fontSize: '0.9rem' }}>
                Actions
              </FormLabel>
              <RichtextEditor value={newActions} onChange={setNewActions} />
              <Typography variant="caption" color="text.secondary">
                Outline the recommended actions or next steps.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={handleCloseDialog}
            color="inherit"
            sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting || isFormInvalid}
            startIcon={submitting && <CircularProgress size={18} color="inherit" />}
            sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
          >
            {submitting ? 'Adding...' : 'Add Recommendation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
